import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { type RootState } from '@/store';
import { MedicalRecordItem } from '@/api';
import { PaginationState, SortingState } from '@tanstack/react-table';

interface MedicalRecordsState {
  medicalRecordsList: MedicalRecordItem[];
  medicalRecordsRowCount: number;
  medicalRecordsPagination: PaginationState;
  medicalRecordsSorting: SortingState;
  // Whether the user has manually changed column sorting. On first load (and
  // after a full page reload) this is false so the request uses the DEFAULT
  // order, even though the table is visually pre-sorted by "Uploaded by".
  medicalRecordsSortingTouched: boolean;
  medicalRecordsFinding: string;
}

const initialState: MedicalRecordsState = {
  medicalRecordsList: [],
  medicalRecordsRowCount: 0,
  medicalRecordsPagination: {
    pageIndex: 0,
    pageSize: 50,
  },
  medicalRecordsSorting: [{ id: 'uploadedBy', desc: true }],
  medicalRecordsSortingTouched: false,
  medicalRecordsFinding: '',
};

const medicalRecordsSlice = createSlice({
  name: 'medicalRecords',
  initialState: initialState,
  reducers: {
    setMedicalRecordsList: (state, action: PayloadAction<MedicalRecordItem[]>) => {
      state.medicalRecordsList = action.payload;
    },
    updateMedicalRecord: (
      state,
      action: PayloadAction<{ documentId: string } & Partial<Omit<MedicalRecordItem, 'documentId'>>>,
    ) => {
      const medicalRecord = state.medicalRecordsList.find((record) => record.documentId === action.payload.documentId);
      if (medicalRecord) {
        Object.assign(medicalRecord, action.payload);
      }
    },
    setMedicalRecordsRowCount: (state, action: PayloadAction<number>) => {
      state.medicalRecordsRowCount = action.payload;
    },
    setMedicalRecordsPagination: (state, action: PayloadAction<PaginationState>) => {
      state.medicalRecordsPagination = action.payload;
    },
    setMedicalRecordsSorting: (state, action: PayloadAction<SortingState>) => {
      state.medicalRecordsSorting = action.payload;
      state.medicalRecordsSortingTouched = true;
    },
    setMedicalRecordsFinding: (state, action: PayloadAction<string>) => {
      state.medicalRecordsFinding = action.payload;
    },
  },
});

export const {
  setMedicalRecordsList,
  updateMedicalRecord,
  setMedicalRecordsRowCount,
  setMedicalRecordsPagination,
  setMedicalRecordsSorting,
  setMedicalRecordsFinding,
} = medicalRecordsSlice.actions;

export const selectMedicalRecordsList = (state: RootState) => {
  return state.medicalRecords.medicalRecordsList;
};
export const selectMedicalRecordsRowCount = (state: RootState) => {
  return state.medicalRecords.medicalRecordsRowCount;
};
export const selectMedicalRecordsPagination = (state: RootState) => {
  return state.medicalRecords.medicalRecordsPagination;
};
export const selectMedicalRecordsSorting = (state: RootState) => {
  return state.medicalRecords.medicalRecordsSorting;
};
export const selectMedicalRecordsSortingTouched = (state: RootState) => {
  return state.medicalRecords.medicalRecordsSortingTouched;
};
export const selectMedicalRecordsFinding = (state: RootState) => {
  return state.medicalRecords.medicalRecordsFinding;
};

export const medicalRecordsReducer = medicalRecordsSlice.reducer;
